import { Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import { usePaymentTotals } from 'modules/payroll_bundle/hooks/usePaymentTotals'
import { UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePayrollPayments } from 'modules/payroll_bundle/lib/payrollPaymentsContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import EmptyState from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/PaymentsPage/Payments/Empty'
import CurrencyWarning from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/_common/CurrencyWarning'
import payments from 'modules/payroll_bundle/store/collections/payments'

import Item from './Item'
import Overview from './Overview'

const Payments = () => {
  const { paymentDispatch } = usePayrollPayments()
  const { currentPeriod, currentPeriodStatus } = usePolicyPeriods()

  const currentPolicyPeriodId = currentPeriod?.id
  const { totals } = usePaymentTotals(currentPolicyPeriodId)

  const toBePaidTotals = totals.find((total) => total.status === 'to_be_paid')
  const employeesWithPendingPaymentsCount = toBePaidTotals?.employeeIds.length ?? 0

  const startPaymentsGeneration = () => {
    paymentDispatch({
      type: UPDATE_DATA,
      payload: {
        showSelectConceptModal: true,
      },
    })

    // Maybe we want to say for which kind of concept here?
    track(trackingEvents.payroll.generatePayment, {
      period_status: currentPeriodStatus,
    })
  }

  const periodPayments = payments.currentPeriodPayments(currentPolicyPeriodId)
  const hasNoPayments = periodPayments.length === 0

  if (hasNoPayments)
    return (
      <EmptyState
        employeesWithPendingPaymentsCount={employeesWithPendingPaymentsCount}
        onStartPaymentsGeneration={startPaymentsGeneration}
      />
    )

  const allCurrencies = new Set<string>()

  periodPayments.map((payment) => {
    payment.paymentItems.map((paymentItem) => {
      const employee = paymentItem?.employee
      const legalEntity = employee && employeeLegalEntity(employee)

      if (legalEntity?.currency) allCurrencies.add(legalEntity?.currency)
    }, [])
  })

  const currencies = [...allCurrencies].sort()

  const currency = currencies[0] || companies.current.attributes.currency

  return (
    <Box paddingTop="s24" gap="s40">
      <Box width="full" gap="s8">
        <Text.Header2>{i18n.t('payroll_bundle.results_payments.payments.title')}</Text.Header2>
        <Box flexDirection="row" gap="s8" alignItems="center">
          <Text.Normal>
            {i18n.t('payroll_bundle.results_payments.payments.description')}
          </Text.Normal>
          <HelpCenterLink name="payroll_results_1">
            <Text.Normal weight="medium">{i18n.t('payroll_bundle.learn_more')}</Text.Normal>
          </HelpCenterLink>
        </Box>
      </Box>
      {currencies.length > 1 && <CurrencyWarning currencies={currencies} currency={currency} />}
      <Overview currency={currency} />
      <Box gap="s24">
        {periodPayments.map((payment) => (
          <Item key={payment.attributes.id} payment={payment} currency={currency} />
        ))}
      </Box>
    </Box>
  )
}

export default observer(Payments)
